import React from 'react';
import {
  Loader2,
  CheckCircle,
  XCircle,
  Clock,
  RefreshCw,
  AlertTriangle,
  ArrowUpRight,
  ArrowDownRight,
} from 'lucide-react';
import { clsx } from 'clsx';
import { formatDistanceToNow } from 'date-fns';
import {
  WorkflowStatus,
  WorkflowResult,
} from '../services/unifiedPricingWorkflowService';

type PricingWorkflowStatusProps = {
  status: WorkflowStatus;
  isExecuting: boolean;
  lastResult: WorkflowResult | null;
  error: string | null;
  compact?: boolean;
  showDetails?: boolean;
};

const statusConfig: Record<
  WorkflowStatus,
  { label: string; icon: React.ElementType; color: string }
> = {
  idle: {
    label: 'Ready',
    icon: Clock,
    color: 'text-gray-500',
  },
  started: {
    label: 'Starting...',
    icon: Loader2,
    color: 'text-blue-500',
  },
  fetching: {
    label: 'Fetching prices...',
    icon: Loader2,
    color: 'text-blue-500',
  },
  calculating: {
    label: 'Calculating...',
    icon: Loader2,
    color: 'text-yellow-500',
  },
  pushing: {
    label: 'Pushing to WebHotelier...',
    icon: Loader2,
    color: 'text-purple-500',
  },
  completed: {
    label: 'Completed',
    icon: CheckCircle,
    color: 'text-green-500',
  },
  failed: {
    label: 'Failed',
    icon: XCircle,
    color: 'text-red-500',
  },
};

export const PricingWorkflowStatus: React.FC<PricingWorkflowStatusProps> = ({
  status,
  isExecuting,
  lastResult,
  error,
  compact = false,
  showDetails = true,
}) => {
  const config = statusConfig[status] || statusConfig.idle;
  const StatusIcon = config.icon;
  const isSpinning = ['started', 'fetching', 'calculating', 'pushing'].includes(
    status
  );

  if (compact) {
    return (
      <div className="flex items-center gap-2">
        <StatusIcon
          className={clsx(
            'h-4 w-4',
            config.color,
            isSpinning && 'animate-spin'
          )}
        />
        <span className={clsx('text-sm', config.color)}>{config.label}</span>
      </div>
    );
  }

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-4 dark:border-slate-700 dark:bg-slate-800">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div
            className={clsx(
              'flex h-10 w-10 items-center justify-center rounded-full',
              status === 'completed' && 'bg-green-100 dark:bg-green-900/30',
              status === 'failed' && 'bg-red-100 dark:bg-red-900/30',
              isExecuting && 'bg-blue-100 dark:bg-blue-900/30',
              status === 'idle' &&
                'bg-gray-100 dark:bg-slate-800 dark:bg-slate-700'
            )}
          >
            <StatusIcon
              className={clsx(
                'h-5 w-5',
                config.color,
                isSpinning && 'animate-spin'
              )}
            />
          </div>
          <div>
            <p className="font-medium text-gray-900 dark:text-slate-100 dark:text-white">
              {config.label}
            </p>
            {lastResult?.completedAt && (
              <p className="text-sm text-gray-500 dark:text-slate-400">
                {formatDistanceToNow(new Date(lastResult.completedAt), {
                  addSuffix: true,
                })}
              </p>
            )}
          </div>
        </div>

        {isExecuting && (
          <div className="flex items-center gap-2 text-sm text-gray-500">
            <RefreshCw className="h-4 w-4 animate-spin" />
            Processing...
          </div>
        )}
      </div>

      {error && (
        <div className="mt-3 flex items-start gap-2 rounded-md bg-red-50 p-3 dark:bg-red-900/20">
          <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0 text-red-500" />
          <p className="min-w-0 break-words text-sm text-red-700 dark:text-red-300">
            {error}
          </p>
        </div>
      )}

      {showDetails && lastResult && !isExecuting && (
        <div className="mt-4 grid grid-cols-2 gap-4 border-t border-gray-200 pt-4 dark:border-slate-700 sm:grid-cols-4">
          <div>
            <p className="text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-slate-400">
              Fetched
            </p>
            <p className="mt-1 text-lg font-semibold text-gray-900 dark:text-slate-100 dark:text-white">
              {lastResult.pricesFetched}
            </p>
          </div>
          <div>
            <p className="text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-slate-400">
              Calculated
            </p>
            <p className="mt-1 text-lg font-semibold text-gray-900 dark:text-slate-100 dark:text-white">
              {lastResult.pricesCalculated}
            </p>
          </div>
          <div>
            <p className="text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-slate-400">
              Pushed
            </p>
            <p className="mt-1 flex items-center gap-1 text-lg font-semibold text-green-600 dark:text-green-400">
              {lastResult.pricesPushed}
              {lastResult.pricesPushed > 0 && (
                <ArrowUpRight className="h-4 w-4" />
              )}
            </p>
          </div>
          <div>
            <p className="text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-slate-400">
              Skipped
            </p>
            <p className="mt-1 flex items-center gap-1 text-lg font-semibold text-gray-500">
              {lastResult.pricesSkipped}
              {lastResult.pricesSkipped > 0 && (
                <ArrowDownRight className="h-4 w-4" />
              )}
            </p>
          </div>
        </div>
      )}

      {showDetails &&
        lastResult?.priceChanges &&
        lastResult.priceChanges.length > 0 && (
          <div className="mt-4 border-t border-gray-200 pt-4 dark:border-slate-700">
            <p className="mb-2 text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-slate-400">
              Recent Price Changes
            </p>
            <div className="max-h-32 space-y-1 overflow-y-auto">
              {lastResult.priceChanges.slice(0, 5).map((change, index) => (
                <div
                  key={index}
                  className="flex items-center justify-between text-sm"
                >
                  <span className="text-gray-600 dark:text-slate-300">
                    {change.roomType} - {change.date}
                  </span>
                  <span
                    className={clsx(
                      'font-medium',
                      change.adjustment > 0
                        ? 'text-green-600 dark:text-green-400'
                        : change.adjustment < 0
                          ? 'text-red-600 dark:text-red-400'
                          : 'text-gray-500'
                    )}
                  >
                    {change.oldPrice.toFixed(2)} → {change.newPrice.toFixed(2)}
                    {change.adjustment !== 0 && (
                      <span className="ml-1">
                        ({change.adjustment > 0 ? '+' : ''}
                        {change.adjustment.toFixed(2)})
                      </span>
                    )}
                  </span>
                </div>
              ))}
              {lastResult.priceChanges.length > 5 && (
                <p className="text-xs text-gray-400">
                  ... and {lastResult.priceChanges.length - 5} more
                </p>
              )}
            </div>
          </div>
        )}
    </div>
  );
};

export default PricingWorkflowStatus;
